import React, { useEffect, useMemo, useRef, useState } from "react";
import type { PostMedia } from "../../types";
import styles from "./MediaCarousel.module.scss";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";

type Props = {
  media?: PostMedia[];
  className?: string;
  onIndexChange?: (idx: number) => void;
  imageFallbackUrl?: string;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

/** Check if a URL is a YouTube/Vimeo link that needs iframe embedding */
function isEmbeddableUrl(url: string): boolean {
  return /youtube\.com|youtu\.be|vimeo\.com/i.test(url);
}

/** Extract YouTube video ID from URL */
function getYouTubeId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  return match ? match[1] : null;
}

/** Get a high-res YouTube thumbnail URL */
function getYouTubeThumbnail(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

/** Convert a YouTube/Vimeo watch URL to an embeddable URL (autoplay on) */
function toEmbedUrl(url: string): string {
  const ytId = getYouTubeId(url);
  if (ytId) {
    return `https://www.youtube.com/embed/${ytId}?autoplay=1&enablejsapi=1`;
  }
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
  }
  return url;
}

export default function MediaCarousel({ media, className, onIndexChange, imageFallbackUrl }: Props) {
  const items = useMemo(
    () => (media ?? []).filter((m) => m.type === "image" || m.type === "video"),
    [media]
  );

  const [index, setIndex] = useState(0);
  const [dragX, setDragX] = useState(0); // px offset while dragging
  const [activeVideoSlide, setActiveVideoSlide] = useState<number | null>(null);
  const draggingRef = useRef(false);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const iframeRefs = useRef<Map<number, HTMLIFrameElement>>(new Map());
  const startRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const widthRef = useRef(1);

  // keep index valid if items change
  useEffect(() => {
    setIndex((i) => clamp(i, 0, Math.max(0, items.length - 1)));
  }, [items.length]);

  const set = (next: number) => {
    const clamped = clamp(next, 0, items.length - 1);
    // Pause any playing video/iframe on the current slide before leaving
    if (clamped !== index) {
      pauseSlide(index);
      setActiveVideoSlide(null);
    }
    setIndex(clamped);
    onIndexChange?.(clamped);
  };

  /** Pause media on a given slide index */
  const pauseSlide = (slideIdx: number) => {
    // Pause YouTube/Vimeo iframes via postMessage
    const iframe = iframeRefs.current.get(slideIdx);
    if (iframe?.contentWindow) {
      // YouTube pause command
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
        "*"
      );
      // Vimeo pause command
      iframe.contentWindow.postMessage(JSON.stringify({ method: "pause" }), "*");
    }
    // Pause native <video> elements
    const slide = rootRef.current?.querySelectorAll(`.${styles.slide}`)?.[slideIdx];
    const video = slide?.querySelector("video");
    if (video) {
      video.pause();
    }
  };

  const isControl = (t: EventTarget | null) => {
    const el = t as HTMLElement | null;
    return !!el?.closest?.("button");
  };

  const prev = () => set(index - 1);
  const next = () => set(index + 1);

  const onPointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (isControl(e.target)) return; // allow nav/dots to click normally

    if (!rootRef.current) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;

    widthRef.current = rootRef.current.clientWidth || 1;
    draggingRef.current = true;
    startRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    setDragX(0);

    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (!draggingRef.current || !startRef.current) return;

    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;

    // If user is mostly scrolling vertically, do nothing (lets feed scroll)
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) return;

    // follow finger/mouse horizontally, with a little resistance at edges
    const atStart = index === 0 && dx > 0;
    const atEnd = index === items.length - 1 && dx < 0;
    const resistance = atStart || atEnd ? 0.35 : 1;

    setDragX(dx * resistance);
  };

  const onPointerUp: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (!draggingRef.current || !startRef.current) return;

    draggingRef.current = false;

    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    const dt = Math.max(1, Date.now() - startRef.current.t);
    startRef.current = null;

    // snap back visually
    setDragX(0);

    // ignore if it was mostly vertical
    if (Math.abs(dy) > Math.abs(dx)) return;

    // threshold: either distance or quick fling
    const width = widthRef.current;
    const distancePass = Math.abs(dx) > Math.max(40, width * 0.18);
    const velocity = Math.abs(dx) / dt; // px/ms
    const flingPass = velocity > 0.8 && Math.abs(dx) > 30;

    if (!distancePass && !flingPass) return;

    if (dx > 0) prev();
    else next();
  };

  if (!items.length) return null;

  const translate = `translateX(calc(-${index * 100}% + ${dragX}px))`;

  return (
    <div
      ref={rootRef}
      className={`${styles.carousel} ${className ?? ""}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      aria-label="Post media carousel"
    >
      <div
        className={styles.track}
        style={
          {
            transform: translate,
          } as React.CSSProperties
        }
        data-dragging={draggingRef.current ? "true" : "false"}
      >
        {items.map((m, slideIdx) => (
          <div className={styles.slide} key={m.id}>
            {m.type === "image" ? (
              <img
                className={styles.media}
                src={m.url}
                alt="Post media"
                loading="lazy"
                onError={(e) => {
                  if (imageFallbackUrl && e.currentTarget.src !== imageFallbackUrl) {
                    e.currentTarget.src = imageFallbackUrl;
                  }
                }}
              />
            ) : isEmbeddableUrl(m.url) ? (
              <div className={styles.videoWrap}>
                {activeVideoSlide === slideIdx ? (
                  // Iframe loaded after user taps play
                  <iframe
                    ref={(el) => {
                      if (el) iframeRefs.current.set(slideIdx, el);
                      else iframeRefs.current.delete(slideIdx);
                    }}
                    className={styles.embedMedia}
                    src={toEmbedUrl(m.url)}
                    title="Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ border: "none" }}
                  />
                ) : (
                  // Thumbnail + play button — fully swipeable
                  <>
                    <img
                      className={styles.media}
                      src={getYouTubeId(m.url)
                        ? getYouTubeThumbnail(getYouTubeId(m.url)!)
                        : imageFallbackUrl || ""}
                      alt="Video thumbnail"
                      loading="lazy"
                      onError={(e) => {
                        if (imageFallbackUrl && e.currentTarget.src !== imageFallbackUrl) {
                          e.currentTarget.src = imageFallbackUrl;
                        }
                      }}
                    />
                    <button
                      type="button"
                      className={styles.playOverlay}
                      onClick={() => setActiveVideoSlide(slideIdx)}
                      aria-label="Play video"
                    >
                      <Play size={48} fill="white" />
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className={styles.videoWrap}>
                <video className={styles.media} src={m.url} muted playsInline autoPlay loop preload="metadata" />
                <div className={styles.playBadge} aria-hidden="true">
                  <Play size={16} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {items.length > 1 && (
        <>
          <button
            type="button"
            className={`${styles.nav} ${styles.left}`}
            onClick={prev}
            aria-label="Previous media"
            disabled={index === 0}
          >
            <ChevronLeft size={18} />
          </button>

          <button
            type="button"
            className={`${styles.nav} ${styles.right}`}
            onClick={next}
            aria-label="Next media"
            disabled={index === items.length - 1}
          >
            <ChevronRight size={18} />
          </button>

          <div className={styles.dots} aria-label="Carousel pagination">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`${styles.dot} ${i === index ? styles.activeDot : ""}`}
                onClick={() => set(i)}
                aria-label={`Go to media ${i + 1}`}
                aria-current={i === index ? "true" : "false"}
              >
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
